import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { FiPlus, FiEdit2, FiTrash2, FiInfo, FiArrowLeft, FiImage } from 'react-icons/fi';
import Logo from '../components/Logo';
import styles from './MenuManagement.module.css';

export default function MenuManagement() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentItem, setCurrentItem] = useState(null);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: '',
    is_available: true,
    image_file: null,
    image_preview: null
  });
  const [uploading, setUploading] = useState(false);

  // Load restaurant data and menu categories
  useEffect(() => {
    const loadRestaurantData = async () => {
      try {
        const { data: restaurant, error: restaurantError } = await supabase
          .from('restaurants')
          .select('*')
          .eq('id', restaurantId)
          .single();

        if (restaurantError) throw restaurantError;
        
        setRestaurant(restaurant);
        
        const { data: categories, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('display_order', { ascending: true });
          
        if (categoriesError) throw categoriesError;
        
        setCategories(categories);
        
        if (categories && categories.length > 0) {
          setSelectedCategory(categories[0].id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading restaurant data:', error);
        setLoading(false);
      }
    };
    
    loadRestaurantData();
  }, [restaurantId]);
  
  // Load menu items when category changes
  useEffect(() => {
    if (!selectedCategory) return;
    
    const loadMenuItems = async () => {
      try {
        const { data, error } = await supabase
          .from('menu_items')
          .select('*')
          .eq('category_id', selectedCategory)
          .order('display_order', { ascending: true });
          
        if (error) throw error;
        
        setMenuItems(data || []);
      } catch (error) {
        console.error('Error loading menu items:', error);
      }
    };
    
    loadMenuItems();
  }, [selectedCategory]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
  };
  
  const handleAddCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!newCategoryName.trim()) return;
    
    try {
      const newDisplayOrder = categories.length;
      
      const { data, error } = await supabase
        .from('categories')
        .insert([
          { 
            name: newCategoryName, 
            restaurant_id: restaurantId, 
            display_order: newDisplayOrder 
          }
        ])
        .select();
        
      if (error) throw error;
      
      console.log("New category created:", data);
      setCategories([...categories, data[0]]);
      setShowAddCategoryForm(false);
      setNewCategoryName('');
      setSelectedCategory(data[0].id);
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };
  
  const handleAddItemClick = () => {
    setNewItem({
      name: '',
      description: '',
      price: '',
      is_available: true,
      image_file: null,
      image_preview: null
    });
    setShowAddItemModal(true);
  };
  
  // Handle file selection for menu item images
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB limit)
      if (file.size > 2000000) {
        alert('Image is too large. Please select an image under 2MB.');
        e.target.value = null; // Clear the input
        return;
      }
      
      setNewItem({...newItem, image_file: file});
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewItem(prev => ({...prev, image_preview: reader.result}));
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Function to upload image to Supabase storage
  const uploadImage = async (file) => {
    try {
      setUploading(true);
      
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${restaurantId}/${fileName}`;
      
      // Upload the file
      const { data, error } = await supabase.storage
        .from('menu-item-images')
        .upload(filePath, file);
        
      if (error) throw error;
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('menu-item-images')
        .getPublicUrl(filePath);
        
      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };
  
  // Function to delete an image from Supabase storage
  const deleteImage = async (url) => {
    if (!url) return;
    
    try {
      // Extract the path from the URL
      // The URL format will be like: https://[project-ref].supabase.co/storage/v1/object/public/menu-item-images/[restaurant-id]/[filename]
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const restaurantFolder = urlParts[urlParts.length - 2];
      const filePath = `${restaurantFolder}/${fileName}`;
      
      // Delete the file
      const { error } = await supabase.storage
        .from('menu-item-images')
        .remove([filePath]);
        
      if (error) {
        console.error('Error deleting image:', error);
      }
    } catch (error) {
      console.error('Error parsing image URL for deletion:', error);
    }
  };
  
  const handleAddItem = async (e) => {
    e.preventDefault();
    
    try {
      let imageUrl = '';
      
      // Upload the image if one is selected
      if (newItem.image_file) {
        imageUrl = await uploadImage(newItem.image_file);
      }
      
      const { data, error } = await supabase
        .from('menu_items')
        .insert([
          { 
            name: newItem.name, 
            description: newItem.description,
            price: parseFloat(newItem.price),
            category_id: selectedCategory,
            restaurant_id: restaurantId,
            active: newItem.is_available,
            image_url: imageUrl,
            display_order: menuItems.length
          }
        ])
        .select();
        
      if (error) throw error;
      
      console.log("New menu item created:", data);
      setMenuItems([...menuItems, data[0]]);
      setShowAddItemModal(false);
      setNewItem({
        name: '',
        description: '',
        price: '',
        is_available: true,
        image_file: null,
        image_preview: null
      });
    } catch (error) {
      console.error('Error adding menu item:', error);
    }
  };
  
  const handleEditItemClick = (item) => {
    setCurrentItem(item);
    setNewItem({
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      is_available: item.active,
      image_file: null,
      image_preview: item.image_url
    });
    setShowEditItemModal(true);
  };
  
  const handleUpdateItem = async (e) => {
    e.preventDefault();
    
    try {
      let imageUrl = currentItem.image_url;
      
      // Upload the new image if one is selected
      if (newItem.image_file) {
        // If there's an existing image, delete it first
        if (currentItem.image_url) {
          await deleteImage(currentItem.image_url);
        }
        imageUrl = await uploadImage(newItem.image_file);
      }
      
      const { error } = await supabase
        .from('menu_items')
        .update({ 
          name: newItem.name, 
          description: newItem.description,
          price: parseFloat(newItem.price),
          active: newItem.is_available,
          image_url: imageUrl
        })
        .eq('id', currentItem.id);
        
      if (error) throw error;
      
      // Update local state
      setMenuItems(menuItems.map(item => 
        item.id === currentItem.id 
          ? { 
              ...item, 
              name: newItem.name, 
              description: newItem.description,
              price: parseFloat(newItem.price),
              active: newItem.is_available,
              image_url: imageUrl
            } 
          : item
      ));
      
      setShowEditItemModal(false);
      setCurrentItem(null);
      setNewItem({
        name: '',
        description: '',
        price: '',
        is_available: true,
        image_file: null,
        image_preview: null
      });
    } catch (error) {
      console.error('Error updating menu item:', error);
    }
  };
  
  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }
    
    try {
      // Get the item to find its image URL
      const itemToDelete = menuItems.find(item => item.id === itemId);
      
      // Delete the image if it exists
      if (itemToDelete?.image_url) {
        await deleteImage(itemToDelete.image_url);
      }
      
      const { error } = await supabase
        .from('menu_items')
        .delete()
        .eq('id', itemId);
        
      if (error) throw error;
      
      setMenuItems(menuItems.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting menu item:', error);
    }
  };
  
  const toggleItemAvailability = async (itemId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .update({ active: !currentStatus }) // Note: schema uses "active" not "is_available"
        .eq('id', itemId);
        
      if (error) throw error;
      
      setMenuItems(menuItems.map(item => 
        item.id === itemId 
          ? { ...item, active: !currentStatus } // Note: schema uses "active" not "is_available"
          : item
      ));
    } catch (error) {
      console.error('Error updating item availability:', error);
    }
  };

  if (loading && !restaurant) return <div className={styles.mmContainer}>Loading restaurant data...</div>;

  return (
    <div className={styles.container}>
      <div className={styles.contentContainer}>
        {/* Page header with back button */}
        <div className={styles.pageHeader}>
          <h1>{restaurant?.name || 'Menu Management'}</h1>
          <button 
            onClick={() => navigate('/dashboard')}
            className={styles.backButton}
          >
            <FiArrowLeft size={16} /> Back to Dashboard
          </button>
        </div>
      
        <div className={styles.contentLayout}>
          <div className={styles.mmSidebar}>
            <h2 className={styles.mmSectionTitle}>Categories</h2>
            
            <div className={styles.mmCategoryList}>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`${styles.mmCategoryButton} ${
                    selectedCategory === category.id ? styles.mmSelected : ''
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            
            {showAddCategoryForm ? (
              <form 
                className={styles.mmAddCategoryForm}
                onSubmit={handleAddCategorySubmit}
              >
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Category name"
                  className={styles.mmInput}
                  required
                />
                <div className={styles.mmFormButtons}>
                  <button 
                    type="button" 
                    onClick={() => setShowAddCategoryForm(false)}
                    className={styles.mmSecondaryButton}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className={styles.mmPrimaryButton}
                  >
                    Add
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setShowAddCategoryForm(true)}
                className={styles.mmAddButton}
              >
                <FiPlus size={16} /> Add Category
              </button>
            )}
          </div>
          
          <div className={styles.mmMainContent}>
            {selectedCategory && (
              <>
                <div className={styles.mmSectionHeader}>
                  <h2 className={styles.mmSectionTitle}>
                    {categories.find(c => c.id === selectedCategory)?.name || 'Menu Items'}
                  </h2>
                  <button
                    onClick={handleAddItemClick}
                    className={styles.mmPrimaryButton}
                    disabled={!selectedCategory}
                  >
                    <FiPlus size={16} /> Add Item
                  </button>
                </div>
                
                <div className={styles.mmMenuGrid}>
                  {menuItems.length === 0 ? (
                    <div className={styles.mmEmptyState}>
                      <FiInfo size={48} />
                      <p>No items in this category yet.</p>
                      <p>Click "Add Item" to create your first menu item.</p>
                    </div>
                  ) : (
                    menuItems.map(item => (
                      <div 
                        key={item.id} 
                        className={styles.mmMenuItem}
                      >
                        {/* Add image display here */}
                        {item.image_url && (
                          <div className={styles.mmMenuItemImageContainer}>
                            <img 
                              src={item.image_url} 
                              alt={item.name}
                              className={styles.mmMenuItemImage} 
                            />
                          </div>
                        )}
                        <div className={styles.mmMenuItemHeader}>
                          <div className={styles.mmMenuItemName}>{item.name}</div>
                          <div className={styles.mmMenuItemPrice}>${item.price.toFixed(2)}</div>
                        </div>
                        <div className={styles.mmMenuItemDesc}>
                          {item.description || 'No description provided.'}
                        </div>
                        <div className={styles.mmMenuItemFooter}>
                          <div 
                            className={`${styles.mmItemStatus} ${item.active ? styles.mmAvailable : styles.mmUnavailable}`}
                            onClick={() => toggleItemAvailability(item.id, item.active)}
                          >
                            {item.active ? 'Available' : 'Unavailable'}
                          </div>
                          <div className={styles.mmMenuItemActions}>
                            <button 
                              onClick={() => handleEditItemClick(item)}
                              className={styles.mmEditButton}
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteItem(item.id)}
                              className={styles.mmDeleteButton}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className={styles.mmModal}>
          <div className={styles.mmModalContent}>
            <div className={styles.mmModalHeader}>
              <h3>Add Menu Item</h3>
              <button 
                onClick={() => setShowAddItemModal(false)}
                className={styles.mmCloseButton}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleAddItem}>
              <div className={styles.mmFormGroup}>
                <label htmlFor="item-name">Item Name</label>
                <input
                  id="item-name"
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                  required
                />
              </div>
              
              <div className={styles.mmFormGroup}>
                <label htmlFor="item-description">Description (optional)</label>
                <textarea
                  id="item-description"
                  value={newItem.description}
                  onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                />
              </div>
              
              <div className={styles.mmFormGroup}>
                <label htmlFor="item-price">Price ($)</label>
                <input
                  id="item-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newItem.price}
                  onChange={(e) => setNewItem({...newItem, price: e.target.value})}
                  required
                />
              </div>
              
              {/* Add image upload form group */}
              <div className={styles.mmFormGroup}>
                <label htmlFor="item-image">Item Image (optional)</label>
                <div className={styles.mmImageUploadContainer}>
                  <input
                    type="file"
                    id="item-image"
                    accept="image/*"
                    onChange={handleFileChange}
                    className={styles.mmFileInput}
                  />
                  <label htmlFor="item-image" className={styles.mmFileInputLabel}>
                    <FiImage size={18} />
                    {newItem.image_file ? 'Change Image' : 'Upload Image'}
                  </label>
                </div>
                
                {newItem.image_preview && (
                  <div className={styles.mmImagePreviewContainer}>
                    <img 
                      src={newItem.image_preview} 
                      alt="Preview" 
                      className={styles.mmImagePreview} 
                    />
                  </div>
                )}
              </div>
              
              <div className={styles.mmFormGroup}>
                <label className={styles.mmCheckboxLabel}>
                  <input
                    type="checkbox"
                    checked={newItem.is_available}
                    onChange={(e) => setNewItem({...newItem, is_available: e.target.checked})}
                  />
                  Item is available
                </label>
              </div>
              
              <div className={styles.mmModalActions}>
                <button 
                  type="button" 
                  onClick={() => setShowAddItemModal(false)}
                  className={styles.mmSecondaryButton}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={styles.mmPrimaryButton}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Item Modal */}
      {showEditItemModal && (
        <div className={styles.mmModal}>
          <div className={styles.mmModalContent}>
            <div className={styles.mmModalHeader}>
              <h3>Edit Menu Item</h3>
              <button 
                onClick={() => setShowEditItemModal(false)}
                className={styles.mmCloseButton}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleUpdateItem}>
              <div className={styles.mmFormGroup}>
                <label htmlFor="edit-item-name">Item Name</label>
                <input
                  id="edit-item-name"
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                  required
                />
              </div>
              
              <div className={styles.mmFormGroup}>
                <label htmlFor="edit-item-description">Description (optional)</label>
                <textarea
                  id="edit-item-description"
                  value={newItem.description}
                  onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                />
              </div>
              
              <div className={styles.mmFormGroup}>
                <label htmlFor="edit-item-price">Price ($)</label>
                <input
                  id="edit-item-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newItem.price}
                  onChange={(e) => setNewItem({...newItem, price: e.target.value})}
                  required
                />
              </div>
              
              {/* Add image upload form group */}
              <div className={styles.mmFormGroup}>
                <label htmlFor="edit-item-image">Item Image (optional)</label>
                <div className={styles.mmImageUploadContainer}>
                  <input
                    type="file"
                    id="edit-item-image"
                    accept="image/*"
                    onChange={handleFileChange}
                    className={styles.mmFileInput}
                  />
                  <label htmlFor="edit-item-image" className={styles.mmFileInputLabel}>
                    <FiImage size={18} />
                    {newItem.image_file ? 'Change Image' : newItem.image_preview ? 'Replace Image' : 'Upload Image'}
                  </label>
                </div>
                
                {newItem.image_preview && (
                  <div className={styles.mmImagePreviewContainer}>
                    <img 
                      src={newItem.image_preview} 
                      alt="Preview" 
                      className={styles.mmImagePreview} 
                    />
                    {newItem.image_preview && !newItem.image_file && currentItem?.image_url && (
                      <button 
                        type="button"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to remove this image?')) {
                            setNewItem({...newItem, image_preview: null});
                          }
                        }}
                        className={styles.mmRemoveImageButton}
                      >
                        Remove Image
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              <div className={styles.mmFormGroup}>
                <label className={styles.mmCheckboxLabel}>
                  <input
                    type="checkbox"
                    checked={newItem.is_available}
                    onChange={(e) => setNewItem({...newItem, is_available: e.target.checked})}
                  />
                  Item is available
                </label>
              </div>
              
              <div className={styles.mmModalActions}>
                <button 
                  type="button" 
                  onClick={() => setShowEditItemModal(false)}
                  className={styles.mmSecondaryButton}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={styles.mmPrimaryButton}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Update Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
